import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import FnrButton from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";

import { getAmountInCents } from "Utilities/Amount/getAmountInCents";

import API from "../../API";
import {
  editModalPanels,
  useRecurringGifts,
} from "../../RecurringGiftsContext";

import {
  useAddonGiftsAsPayload,
  useAmount,
  useDeletedAddOnGifts,
  useSaveButtonText,
} from "./stateHooks";

export default function SaveButton() {
  const { state, dispatch, onSuccessfulSave } = useRecurringGifts();
  const { giftWithEdits, currentGift, isSubmitting } = state.editModal;
  const [submitStatus, setSubmitStatus] = useState(null);
  const saveBtnRef = useRef();
  const addOnGifts = useAddonGiftsAsPayload();
  const [deletedAddOnGifts] = useDeletedAddOnGifts();
  const [amount] = useAmount();
  const saveButtonText = useSaveButtonText();
  const paymentMethodChanged =
    currentGift?.paymentMethod.ID !== giftWithEdits?.paymentMethod?.ID;

  const isUnusable = state?.editModal?.giftWithEdits?.paymentMethod?.isUnusable;

  const validateForSave = () => {
    let error = null;

    if (!amount || amount < 2 || amount > 100000) {
      error = "amount";
    } else if (!giftWithEdits.interval) {
      error = "interval";
    } else if (!giftWithEdits.startDate) {
      error = "startDate";
    } else if (!giftWithEdits.paymentMethod.ID) {
      error = "paymentMethod";
    } else if (isUnusable) {
      error = "isUnusable";
    } else if (!giftWithEdits.ID) {
      error = "general";
    }

    if (error) {
      dispatch({
        type: "setEditPanelValidationError",
        payload: error,
      });
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!validateForSave()) return;

    dispatch({
      type: "setIsSubmitting",
      payload: true,
    });

    const submitData = {
      ID: giftWithEdits.ID,
      amount: getAmountInCents(amount),
      interval: giftWithEdits.interval,
      startDate: giftWithEdits.startDate,
      endDate: giftWithEdits.endDate,
      paymentMethodID: giftWithEdits.paymentMethod.ID,
      applyToExistingPledge: giftWithEdits.applyToExistingPledge,
      memo: giftWithEdits.memo,
      addOnGifts,
      deletedAddOnGifts,
    };

    API.editRecurringGift(submitData)
      .post()
      .then((res) => {
        handleSuccessfulSaveResponse(res);
        if (paymentMethodChanged && onSuccessfulSave) onSuccessfulSave();
      })
      .catch((err) => {
        setSubmitStatus("error");
      })
      .finally(() => {
        dispatch({
          type: "setIsSubmitting",
          payload: false,
        });
      });
  };

  const handleSuccessfulSaveResponse = (res) => {
    dispatch({
      type: "setRecurringGifts",
      payload: res.recurringGifts,
    });

    dispatch({
      type: "setThirtyDayTotal",
      payload: res.thirtyDayTotal,
    });

    dispatch({
      type: "setCurrentPanel",
      payload: editModalPanels.initial,
    });

    dispatch({
      type: "setDuplicatePaymentForTodayError",
      payload: res.duplicatePaymentForTodayError,
    });
  };
  return (
    <>
      <FnrButton
        data-cy="saveEditRecurringBtn"
        ref={saveBtnRef}
        onClick={handleSaveClick}
        color="blue"
        loading={isSubmitting}
        disabled={isSubmitting}
        role="button"
        aria-label="save-button"
      >
        {saveButtonText}
      </FnrButton>

      <FnrPopover
        show={submitStatus === "error"}
        buttonRef={saveBtnRef}
        onShouldClose={() => setSubmitStatus(null)}
      >
        <SubmitErrorContent>
          Oh no! Something went wrong. Please try again, or contact{" "}
          <a href="mailto:help@flocknote.com" target="_blank" rel="noreferrer">
            help@flocknote.com
          </a>{" "}
          for assistance.
          <div style={{ marginTop: 10 }}>
            <FnrButton onClick={() => setSubmitStatus(null)} color="blue">
              Got it
            </FnrButton>
          </div>
        </SubmitErrorContent>
      </FnrPopover>
    </>
  );
}

const SubmitErrorContent = styled.div`
  width: 280px;
  padding: 10px;
`;
